/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import getToken from "../../../utils/getToken";

const API_URL = process.env.REACT_APP_API_URL;

type Platform = "ANDROID" | "IOS";

interface AppVersionConfig {
  platform: Platform;
  minRequiredBuild: number;
  latestBuild: number;
  latestVersionName: string;
  updateEnabled: boolean;
  storeUrl: string;
  title: string;
  message: string;
}

const emptyConfig = (platform: Platform): AppVersionConfig => ({
  platform,
  minRequiredBuild: 1,
  latestBuild: 1,
  latestVersionName: "",
  updateEnabled: true,
  storeUrl: "",
  title: "Update Available",
  message: "A new version of the app is available. Please update to continue.",
});

const cardStyle = { backgroundColor: "#000F1E" };
const labelClass = "form-label text-white fw-semibold fs-7";
const inputClass = "form-control";
// Inline styles override Metronic's white form-control background so typed text is visible.
const inputStyle: React.CSSProperties = {
  backgroundColor: "#0B1220",
  color: "#FFFFFF",
  border: "1px solid #2B3B52",
};

const PlatformCard: FC<{
  platform: Platform;
  initial?: AppVersionConfig;
}> = ({ platform, initial }) => {
  const [cfg, setCfg] = useState<AppVersionConfig>(
    initial ?? emptyConfig(platform)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) setCfg(initial);
  }, [initial]);

  const set = (patch: Partial<AppVersionConfig>) =>
    setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    if (Number(cfg.minRequiredBuild) > Number(cfg.latestBuild)) {
      toast.error("Minimum build cannot be greater than latest build");
      return;
    }
    const loadingToastId = toast.loading("Saving...");
    setSaving(true);
    try {
      const token = getToken();
      const config: AxiosRequestConfig = {
        url: `${API_URL}/app-version/edit`,
        method: "patch",
        headers: { Authorization: token },
        data: {
          platform: cfg.platform,
          minRequiredBuild: Number(cfg.minRequiredBuild),
          latestBuild: Number(cfg.latestBuild),
          latestVersionName: cfg.latestVersionName,
          updateEnabled: cfg.updateEnabled,
          storeUrl: cfg.storeUrl,
          title: cfg.title,
          message: cfg.message,
        },
      };
      await axios(config);
      toast.dismiss(loadingToastId);
      toast.success(`${platform} update settings saved`);
    } catch (error: any) {
      toast.dismiss(loadingToastId);
      toast.error(error?.response?.data?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card border-0 mb-5" style={cardStyle}>
      <div className="card-header border-0 pt-6 d-flex align-items-center justify-content-between">
        <h3 className="card-title text-white fw-bold m-0">
          {platform === "ANDROID" ? "Android (Google Play)" : "iOS (App Store)"}
        </h3>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={cfg.updateEnabled}
            onChange={(e) => set({ updateEnabled: e.target.checked })}
            id={`enabled-${platform}`}
          />
          <label
            className="form-check-label text-white ms-2"
            htmlFor={`enabled-${platform}`}
          >
            {cfg.updateEnabled ? "Enabled" : "Disabled"}
          </label>
        </div>
      </div>

      <div className="card-body pt-3">
        <div className="row g-4">
          <div className="col-md-6">
            <label className={labelClass}>
              Minimum required build (force below this)
            </label>
            <input
              type="number"
              min={1}
              className={inputClass}
              style={inputStyle}
              value={cfg.minRequiredBuild}
              onChange={(e) =>
                set({ minRequiredBuild: Number(e.target.value) })
              }
            />
          </div>
          <div className="col-md-6">
            <label className={labelClass}>
              Latest build (soft prompt below this)
            </label>
            <input
              type="number"
              min={1}
              className={inputClass}
              style={inputStyle}
              value={cfg.latestBuild}
              onChange={(e) => set({ latestBuild: Number(e.target.value) })}
            />
          </div>
          <div className="col-md-6">
            <label className={labelClass}>Latest version name</label>
            <input
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="e.g. 3.2.0"
              value={cfg.latestVersionName}
              onChange={(e) => set({ latestVersionName: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <label className={labelClass}>Store URL</label>
            <input
              type="text"
              className={inputClass}
              style={inputStyle}
              value={cfg.storeUrl}
              onChange={(e) => set({ storeUrl: e.target.value })}
            />
          </div>
          <div className="col-12">
            <label className={labelClass}>Dialog title</label>
            <input
              type="text"
              className={inputClass}
              style={inputStyle}
              value={cfg.title}
              onChange={(e) => set({ title: e.target.value })}
            />
          </div>
          <div className="col-12">
            <label className={labelClass}>Dialog message</label>
            <textarea
              className={inputClass}
              style={inputStyle}
              rows={2}
              value={cfg.message}
              onChange={(e) => set({ message: e.target.value })}
            />
          </div>
        </div>

        <div className="text-muted fs-8 mt-4">
          Tip: to force <strong>everyone</strong> onto the latest version, set
          the minimum required build equal to the latest build.
        </div>

        <div className="d-flex justify-content-end mt-5">
          <button
            className="btn btn-success"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : `Save ${platform}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const AppUpdateWrapper: FC = () => {
  const [android, setAndroid] = useState<AppVersionConfig>();
  const [ios, setIos] = useState<AppVersionConfig>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const token = getToken();
        const { data } = await axios({
          url: `${API_URL}/app-version/all`,
          method: "get",
          headers: { Authorization: token },
        });
        const list: AppVersionConfig[] = data?.data ?? [];
        setAndroid(
          list.find((c) => c.platform === "ANDROID") ?? emptyConfig("ANDROID")
        );
        setIos(list.find((c) => c.platform === "IOS") ?? emptyConfig("IOS"));
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ?? "Failed to load app version config"
        );
        setAndroid(emptyConfig("ANDROID"));
        setIos(emptyConfig("IOS"));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return <div className="text-white p-10">Loading app update settings…</div>;
  }

  return (
    <>
      <h1 className="text-white fw-bold mb-5">App Update Control</h1>
      <PlatformCard platform="ANDROID" initial={android} />
      <PlatformCard platform="IOS" initial={ios} />
    </>
  );
};

export { AppUpdateWrapper };
export default AppUpdateWrapper;
